import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import styles from "../styles/Login.module.css";
import { auth, db } from "../firebase";
import { signInWithEmailAndPassword, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

// ── CDN assets ───────────────────────────────────────────────────────────────
const ASSETS = {
  landscapeVideo: "https://cdn.synaptexglobal.com/loginlandscape(1).mp4",
  portraitVideo: "https://cdn.synaptexglobal.com/loginportrait(1).mp4",
  landscapePoster: "https://cdn.synaptexglobal.com/loginlandscapeimage.png",
  portraitPoster: "https://cdn.synaptexglobal.com/loginportrait.png",
};

// ── Main component ────────────────────────────────────────────────────────────
export default function Login() {
  const navigate = useNavigate();
  const [isLandscape, setIsLandscape] = useState(
    () => window.matchMedia("(orientation: landscape)").matches,
  );
  const [assetsReady, setAssetsReady] = useState(false);
  const [loaderFading, setLoaderFading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const videoRef = useRef(null);
  const loadedCount = useRef(0);
  const totalAssets = 2;

  // ── Redirect already-logged-in users ─────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const data = snap.data() || {};
        const uid = user.uid;
        if (data.isAdmin) {
          navigate("/adminconsole", { replace: true });
        } else if (data.category === "merchandiser") {
          navigate("/leads/" + uid, { replace: true });
        } else if (data.category === "quality-control") {
          navigate("/quality/" + uid, { replace: true });
        } else {
          navigate("/plm/" + uid, { replace: true });
        }
      } catch {
        navigate("/plm/" + user.uid, { replace: true });
      }
    });
    return () => unsub();
  }, []);

  // ── Orientation listener ──────────────────────────────────────────────────
  useEffect(() => {
    const mq = window.matchMedia("(orientation: landscape)");
    const handler = (e) => setIsLandscape(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // ── Update video source on orientation change ─────────────────────────────
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const src = isLandscape ? ASSETS.landscapeVideo : ASSETS.portraitVideo;
    if (vid.currentSrc !== src) {
      vid.src = src;
      vid.load();
      vid.play().catch(() => {});
    }
  }, [isLandscape]);

  // ── Asset preloading ──────────────────────────────────────────────────────
  const markLoaded = useCallback(() => {
    loadedCount.current += 1;
    if (loadedCount.current >= totalAssets) {
      setLoaderFading(true);
      setTimeout(() => setAssetsReady(true), 500);
    }
  }, []);

  useEffect(() => {
    [ASSETS.landscapeVideo, ASSETS.portraitVideo].forEach((src) => {
      const vid = document.createElement("video");
      vid.preload = "metadata";
      vid.muted = true;
      vid.src = src;
      const done = () => {
        markLoaded();
        vid.oncanplaythrough = null;
        vid.onerror = null;
      };
      vid.oncanplaythrough = done;
      vid.onerror = done;
      vid.load();
      setTimeout(done, 6000);
    });
  }, []);

  // ── Firebase login ────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setError("");
    if (!username.trim() || !password) {
      setError("Please fill in both fields.");
      return;
    }
    setLoading(true);
    try {
      const email = `${username}@synaptex.app`;
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const uid = userCredential.user.uid;

      const userDoc = await getDoc(doc(db, "users", uid));

      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.isAdmin) {
          navigate("/adminconsole");
        } else if (data.isBuyer) {
          navigate("/plm/" + data.linkedSupplierUid);
        } else if (data.category === "merchandiser") {
          navigate("/leads/" + uid);
        } else if (data.category === "quality-control") {
          navigate("/quality/" + uid);
        } else {
          navigate("/plm/" + uid);
        }
      } else {
        // Fallback: doc missing — treat as supplier using their own uid
        navigate("/plm/" + uid);
      }
    } catch (err) {
      const msgs = {
        "auth/user-not-found": "No account found for that username.",
        "auth/wrong-password": "Incorrect password.",
        "auth/invalid-email": "Invalid username format.",
        "auth/too-many-requests": "Too many attempts — try again later.",
        "auth/invalid-credential": "Invalid username or password.",
      };
      setError(msgs[err.code] || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title> Login | Synaptex Global</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      {/* ── Fullscreen loader ── */}
      {!assetsReady && (
        <div
          className={`${styles.loader} ${loaderFading ? styles.loaderFade : ""}`}
        >
          <div className={styles.loaderSpinner} />
        </div>
      )}

      <div
        className={`${styles.root} ${isLandscape ? styles.landscape : styles.portrait}`}
      >
        {/* ── Background video ── */}
        <video
          ref={videoRef}
          className={styles.bgVideo}
          src={isLandscape ? ASSETS.landscapeVideo : ASSETS.portraitVideo}
          poster={isLandscape ? ASSETS.landscapePoster : ASSETS.portraitPoster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        />

        {/* ── Vignette overlay ── */}
        <div className={styles.vignette} />

        {/* ── Home button top-right ── */}
        <Link to="/" className={styles.homeBtn} aria-label="Go to home">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9.5z" />
            <path d="M9 21V12h6v9" />
          </svg>
        </Link>

        {/* ── Main layout ── */}
        <div className={styles.layout}>
          {isLandscape && <div className={styles.leftCol} />}

          {/* ── Login card ── */}
          <div className={styles.card}>
            <p className={styles.cardLabel}>sign in</p>

            <div className={styles.fields}>
              <div className={styles.fieldWrap}>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="USERNAME"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoComplete="username"
                  spellCheck={false}
                />
              </div>

              <div className={styles.fieldWrap}>
                <input
                  className={styles.input}
                  type={showPass ? "text" : "password"}
                  placeholder="PASSWORD"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowPass((v) => !v)}
                  aria-label={showPass ? "Hide password" : "Show password"}
                >
                  {showPass ? (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && <p className={styles.errorMsg}>{error}</p>}

            <button
              className={`${styles.submitBtn} ${loading ? styles.submitting : ""}`}
              onClick={handleSubmit}
              disabled={loading}
            >
              <span className={styles.submitLabel}>
                {loading ? "SIGNING IN" : "ENTER"}
              </span>
              {loading && <span className={styles.spinner} />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
